// src/screens/Merchant/SelectProductForFlashSaleScreen.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import './SelectProductForFlashSaleScreen.css';

import backIcon from '../../assets/backarrow-icon.png';
import placeholderImg from '../../assets/placeholder-banner.png';

function SelectProductForFlashSaleScreen({ user }) {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState('all'); // all | active | completed
  const [endedSalesMap, setEndedSalesMap] = useState({});
  const [activeSalesMap, setActiveSalesMap] = useState({});

  // toast (inline)
  const [toast, setToast] = useState({ visible: false, message: '', type: '' });
  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast({ visible: false, message: '', type: '' }), 2200);
  };

  // Load merchant products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await api.get(`/products/by-username/${user.username}`);
        const data = Array.isArray(res.data) ? res.data : res.data?.products || [];
        setProducts(data);
      } catch (err) {
        console.error('❌ Failed to load products:', err);
        showToast('Failed to load products', 'error');
      } finally {
        setLoading(false);
      }
    };
    if (user?._id) fetchProducts();
  }, [user]);

  // Load flash sales
  useEffect(() => {
    const fetchSales = async () => {
      try {
        const act = await api.get(
          `/merchants/me/flash-sales?status=active&username=${encodeURIComponent(user?.username || '')}`
        );

        const active = act.data?.sales || [];
        const aMap = {};
        active.forEach(s => {
          aMap[String(s.productId)] = {
            percentOff: s.percentOff,
            endAt: s.endAt,
            saleId: s._id
          };
        });
        setActiveSalesMap(aMap);

        const endedRes = await api.get(
          `/merchants/me/flash-sales?status=ended&username=${encodeURIComponent(user?.username || '')}`
        );

        const ended = endedRes.data?.sales || [];
        const eMap = {};
        ended.forEach(s => {
          const pid = String(s.productId);
          const prev = eMap[pid];
          if (!prev || new Date(s.endAt) > new Date(prev)) {
            eMap[pid] = s.endAt;
          }
        });
        setEndedSalesMap(eMap);
      } catch (err) {
        console.error('❌ Failed to load sales:', err);
      }
    };

    if (user?.username) fetchSales();
  }, [user?.username]);

  // Helpers
  const isOnFlashSale = (p) => {
    if (p?.pricingView?.isOnFlashSale) return true;
    return Boolean(activeSalesMap[String(p._id)]);
  };

  const getActiveSaleInfo = (p) => {
    if (p?.pricingView?.isOnFlashSale) {
      return {
        percentOff: activeSalesMap[String(p._id)]?.percentOff ?? null,
        endAt: p.pricingView.saleEndsAt || activeSalesMap[String(p._id)]?.endAt || null
      };
    }
    return activeSalesMap[String(p._id)] || null;
  };

  // Compute sale math locally using activeSalesMap (fallbacks to pricingView keys)
  const computeSaleView = (p) => {
    const pid = String(p._id);
    const base = p?.pricingView?.basePrice ?? p?.price ?? null;
    const baseNum = Number(base);
    if (!Number.isFinite(baseNum)) return null;

    const mapSale = activeSalesMap[pid] || {};
    const percent =
      mapSale.percentOff ??
      p?.pricingView?.salePercentOff ??
      null;
    if (percent == null) return null;

    const pct = Number(percent);
    const discounted = Math.max(0, Math.round(baseNum * (1 - pct / 100) * 100) / 100);
    const endAt =
      mapSale.endAt ??
      p?.pricingView?.saleEndsAt ??
      null;
    return { base: baseNum, discounted, percent: pct, endAt };
  };

  const formatPi = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return '';
    return x.toFixed(2).replace(/\.00$/, '');
  };

  // Filtered list
  const filtered = useMemo(() => {
    if (filter === 'all') return products;
    if (filter === 'active') return products.filter(p => isOnFlashSale(p));
    if (filter === 'completed') return products.filter(p => endedSalesMap[String(p._id)]);
    return products;
  }, [products, filter, endedSalesMap, activeSalesMap]);

  const handleProceed = () => {
    if (!selectedId) return;
    navigate(`/merchant/flash-sale/new/${selectedId}`);
  };

  return (
    <div className="fsale-page">
      {/* Topbar */}
      <div className="fsale-topbar">
        <img
          src={backIcon}
          alt="Back"
          className="fsale-back-icon"

onClick={() => navigate('/merchant/dashboard')}

        />
        <h2>Flash Sales</h2>
      </div>

      {/* Scrollable content */}
      <div className="fsale-scroll">
        {/* Filters */}
        <div className="fsale-filters">
          {['all', 'active', 'completed'].map(key => (
            <button
              key={key}
              className={`fsale-filter-btn ${filter === key ? 'active' : ''}`}
              onClick={() => { setFilter(key); setSelectedId(null); }}
            >
              {key === 'all' ? 'All' : key.charAt(0).toUpperCase() + key.slice(1)}
            </button>
          ))}
        </div>

        {/* Product list */}
        <div className="fsale-list">
          {loading ? (
            <p className="fsale-loading">Loading products...</p>
          ) : filtered.length === 0 ? (
            <p className="fsale-empty">
              {filter === 'active'
                ? 'No active flash sales right now.'
                : filter === 'completed'
                ? 'No completed flash sales yet.'
                : 'No products found. Add a product first.'}
            </p>
          ) : (
            filtered.map((p) => {
              const sale = getActiveSaleInfo(p);
              const basePrice = p?.pricingView?.basePrice ?? p?.price ?? null;
              const sv = computeSaleView(p); // { base, discounted, percent, endAt } | null

              return (
                <div
                  key={p._id}
                  className={`fsale-card ${selectedId === p._id ? 'selected' : ''}`}
                  onClick={() => setSelectedId(p._id)}
                >
                  <img
                    src={p.media?.[0] || placeholderImg}
                    alt={p.name}
                    className="fsale-img"
                  />
                  <div className="fsale-info">
                    <div className="fsale-title-row">
                      <strong className="fsale-title">{p.name}</strong>
                      {isOnFlashSale(p) && <span className="fsale-badge">Active</span>}
                      {!isOnFlashSale(p) && endedSalesMap[String(p._id)] && (
                        <span className="fsale-badge ended">Completed</span>
                      )}
                    </div>

                    {/* Price block */}
                    {(() => {
                      if (sv) {
                        return (
                          <div className="fsale-price">
                            <div className="fsale-price-line">
                              <span className="fsale-old">{formatPi(sv.base)} Pi</span>
                            </div>
                            <div className="fsale-new">
                              {formatPi(sv.discounted)} Pi
                              {Number.isFinite(sv.percent) && (
                                <span className="fsale-off">−{sv.percent}%</span>
                              )}
                            </div>
                            {sv.endAt && (
                              <div className="fsale-expiry">
                                Ends {new Date(sv.endAt).toLocaleString()}
                              </div>
                            )}
                          </div>
                        );
                      }
                      // Fallback (no sale)
                      return (
                        <div className="fsale-price">
                          <div className="fsale-new">{formatPi(basePrice)} Pi</div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="fsale-footer">
        <button
          className="fsale-proceed-btn"
          disabled={!selectedId}
          onClick={handleProceed}
        >
          Set Flash Sale
        </button>
      </div>

      {/* Toast */}
      {toast.visible && (
        <div className={`fsale-toast ${toast.type}`}>{toast.message}</div>
      )}
    </div>
  );
}

export default SelectProductForFlashSaleScreen;
